import { Navigate, Route, Routes } from 'react-router-dom';
import { useAuth } from './contexts/AuthContext';
import { Layout } from './components/Layout';
import { LoadingState, ErrorState } from './components/Feedback';
import { LoginPage } from './pages/Login';
import { CatalogPage } from './pages/Catalog';
import { ApplicationDetailPage } from './pages/ApplicationDetail';
import { MyAccessPage } from './pages/MyAccess';
import { AdminLayout } from './pages/admin/AdminLayout';
import { AdminOverviewPage } from './pages/admin/AdminOverview';
import { AdminUsersPage } from './pages/admin/AdminUsers';
import { AdminApplicationsPage } from './pages/admin/AdminApplications';
import { AdminAuditPage } from './pages/admin/AdminAudit';
import { AdminAnnouncementsPage } from './pages/admin/AdminAnnouncements';

/**
 * Garde des routes d'administration.
 *
 * Elle ne masque que l'interface. L'API vérifie le rôle de son côté à chaque
 * requête : contourner cette garde côté navigateur ne donne accès à rien.
 */
function RequireAdmin({ children }: { children: React.ReactNode }) {
    const { isAdmin } = useAuth();
    return isAdmin ? <>{children}</> : <Navigate to="/" replace />;
}

export function App() {
    const { isAuthenticated, isLoading, error, user } = useAuth();

    if (isLoading) {
        return (
            <div className="min-h-screen flex items-center justify-center">
                <LoadingState label="Ouverture de votre session…" />
            </div>
        );
    }

    if (!isAuthenticated) {
        // Une session Entra valide mais un profil introuvable côté API signale un
        // problème de configuration, pas un simple défaut de connexion.
        if (error && !user) {
            return (
                <div className="min-h-screen flex items-center justify-center p-4">
                    <ErrorState message={error} onRetry={() => window.location.reload()} />
                </div>
            );
        }
        return <LoginPage />;
    }

    return (
        <Layout>
            <Routes>
                <Route path="/" element={<CatalogPage />} />
                <Route path="/applications/:idOrSlug" element={<ApplicationDetailPage />} />
                <Route path="/mes-acces" element={<MyAccessPage />} />

                <Route
                    path="/admin"
                    element={
                        <RequireAdmin>
                            <AdminLayout />
                        </RequireAdmin>
                    }
                >
                    <Route index element={<AdminOverviewPage />} />
                    <Route path="users" element={<AdminUsersPage />} />
                    <Route path="applications" element={<AdminApplicationsPage />} />
                    <Route path="announcements" element={<AdminAnnouncementsPage />} />
                    <Route path="audit" element={<AdminAuditPage />} />
                </Route>

                <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
        </Layout>
    );
}
